import { usePricingRights } from '@util/PricingRightsHook';
import React, { useContext, useEffect, useState } from 'react';
import Tag from '@app/component/tag/Tag';
import { ArrowDownIcon, ArrowUpIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/20/solid';
import { Tooltip } from 'react-tooltip';
import RenderProviderWithIndicators from '@app/component/provider/RenderProviderWithIndicators';
import Utilities from '@util/Utilities';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { MarketStateStatDateSnapshot } from '@app/model/market_state/MarketStateStatDateSnapshot';
import DisplayGroupName
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/DisplayGroupName';
import DisplayMarketStateItemPrice, { PromotionIndicator }
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/DisplayMarketStateItemPrice';
import {
	FlexIndicator,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/fragment/FlexIndicator';
import MealIndicator
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/fragment/MealIndicator';
import DisplayAvailabilityIndexTag
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/component/DisplayAvailabilityIndexTag';
import MarketStateForDayAvailabilityEvolutionIndicator
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/parity/MarketStateForDayAvailabilityEvolutionIndicator';
import {
	MarketStateCalendarDayTableRowRoomIndicator,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateCalendarDayTableRowRoomIndicator';
import PriceRecommendationStateSnapshot from '@app/model/market_state/output/PriceRecommendationStateSnapshot';
import MarketStateSnapshotCalendarDayTableRowPriceSubmissionAction
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateSnapshotCalendarDayTableRowPriceSubmissionAction';
import {
	AvailabilityDailyStateContext,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import {
	PricingRoomsInventoryContext,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/PricingRoomsInventoryProvider';
import CalendarDay from '@app/state/calendar/CalendarDay';
import MarketStateProofLink
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/proof/MarketStateProofLink';
import { getPromotionIndicatorState }
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/PromotionIndicator.utilities';

const MarketStateSnapshotCalendarDayTableRow: React.FunctionComponent<{
	currentMarketStateForDay: MarketStateStatDateSnapshot
	priceRecommendationState: PriceRecommendationStateSnapshot | undefined
	current: MarketStateStatDateCompetitorSnapshot
	previous: MarketStateStatDateCompetitorSnapshot | undefined,
	calendarDay?: CalendarDay
}> = ({ currentMarketStateForDay, priceRecommendationState, current, calendarDay, previous }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);
	const [isPriceRecommendationSubmitted, setIsPriceRecommendationSubmitted] = useState<boolean>(false);
	const lowestItemForRoom = current.getLowestItemForRoom();
	const lowestItem = lowestItemForRoom?.lowestItem?.item;
	const showPromotion = getPromotionIndicatorState(
		lowestItem?.promotions,
		lowestItem?.preferredTier
	).visible;

	useEffect(() => {
		setIsPriceRecommendationSubmitted(current.isMainHotel && priceRecommendationState?.isSubmitted!);
	}, [priceRecommendationState]);

	return <tr className={current.isMainHotel ? 'bg-sky-50' : undefined}>
		<td className="py-2.5 pl-4 pr-3 text-xs font-medium text-gray-900 sm:pl-6 max-w-[200px] truncate">
			{!current.isGroup && <span title={current.getHotelName(companyHotel)}>{current.getHotelName(companyHotel)}</span>}
			{current.isGroup && <DisplayGroupName group={current.getCompetitor(companyHotel)!} />}
		</td>
		<td className="py-2.5 px-3 text-xs text-gray-500" style={{ width: 80 }}>
			<div className="flex items-center gap-x-1">
				<DisplayMarketStateItemPrice current={current} previous={previous} />
				{isPriceRecommendationSubmitted && <span className="inline-flex text-blue-600" title="Submitted"><span className="sr-only">Submitted</span><CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" /></span>}
			</div>
		</td>
		<td className="w-[52px] py-2.5 px-0 text-center">
			<div className="flex items-center justify-center">
				<FlexIndicator competitorSnapshot={current} checkInDate={currentMarketStateForDay.statDate} />
			</div>
		</td>
		<td className="w-px whitespace-nowrap py-2.5 px-1 text-center text-xs text-gray-500">
			<div className="flex items-center justify-center">
				<MealIndicator competitorSnapshot={current} />
			</div>
		</td>
		<td className="w-px whitespace-nowrap py-2.5 px-1 text-center text-xs text-gray-500">
			{showPromotion && <div className="flex items-center justify-center">
				<PromotionIndicator
					promotions={lowestItem?.promotions}
					preferredTier={lowestItem?.preferredTier}
					currency={lowestItem?.price?.currency}
					variant="icon"
					tooltipId="calendar_day_promotion_tooltip"
				/>
			</div>}
		</td>
		<td className="py-2.5 px-3 text-xs text-gray-500" style={{ width: 166 }}>
			{!calendarDay && <DisplayAvailabilityIndexTag current={current} />}
			{calendarDay && current.isMainHotel &&
				<RenderMainHotelAvailability companyHotel={companyHotel}
				                             current={current}
				                             currentMarketStateForDay={currentMarketStateForDay} />}
			{calendarDay && !current.isMainHotel && <RenderBookingAvailability companyHotel={companyHotel} current={current}
			                                                    currentMarketStateForDay={currentMarketStateForDay} />}

		</td>
		<td className="py-2.5 px-3 text-xs text-gray-500">
			{current.isNotAvailable() && <span />}
			{(current.isAvailable() || current.isLos()) &&
				<div className="flex">
					{!lowestItemForRoom.lowestItem.item.isPartnerOffer &&
						<MarketStateCalendarDayTableRowRoomIndicator
							marketStatDateCompetitorSnapshot={current} />}
					{lowestItemForRoom.lowestItem.item.isPartnerOffer && <Tag state="info">
						Partner offer
						<InformationCircleIcon data-tooltip-id={`partner-offer-tooltip-${lowestItemForRoom.lowestItem.item.id}`}
						                       className="ml-1 w-3.5 h-3.5" />
						<Tooltip id={`partner-offer-tooltip-${lowestItemForRoom.lowestItem.item.id}`}
						         className="z-40 bg-white w-[100px] shadow-2xl ring-1 ring-black ring-opacity-10 rounded-lg text-xs text-gray-900"
						         style={{ backgroundColor: 'white' }}>
							<div className="text-gray-900">{lowestItemForRoom.findRoomName(companyHotel)}</div>
						</Tooltip>
					</Tag>}
				</div>}
		</td>
		<td className="py-2.5 text-sm text-gray-500">
			<div className="flex items-start gap-x-2">
				{(current.isAvailable() || current.isLos() || current.isSoldOut()) &&
					<RenderProviderWithIndicators
						lowestItem={lowestItemForRoom.lowestItem}
						marketStateIds={currentMarketStateForDay.marketStateIds}
						afterProvider={lowestItemForRoom.lowestItem.item.hasProof
							? <MarketStateProofLink companyHotelId={companyHotel.id} item={lowestItemForRoom.lowestItem.item} />
							: undefined}
					/>}
			</div>
		</td>
		{calendarDay && (canViewRecommendations || canChangePrices) && <td className="py-2.5 text-xs text-gray-500">
			{current.isMainHotel && !calendarDay.isPast &&
				< MarketStateSnapshotCalendarDayTableRowPriceSubmissionAction
					priceRecommendationState={priceRecommendationState} statDate={currentMarketStateForDay.statDate} />}
		</td>}
	</tr>;
};
;

const RenderMainHotelAvailability: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	current: MarketStateStatDateCompetitorSnapshot,
	currentMarketStateForDay: MarketStateStatDateSnapshot
}> = ({ companyHotel, current, currentMarketStateForDay }) => {
	const { hasData, isLoading } = React.useContext(AvailabilityDailyStateContext);

	if (isLoading) return null;

	return hasData ? <RenderPmsAvailability tooltipId={`pms-availability-tooltip-${currentMarketStateForDay.statDate}`}
	                                        companyHotel={companyHotel} />
		: <RenderBookingAvailability companyHotel={companyHotel}
		                             current={current}
		                             currentMarketStateForDay={currentMarketStateForDay} />;
};

const RenderBookingAvailability: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	current: MarketStateStatDateCompetitorSnapshot,
	currentMarketStateForDay: MarketStateStatDateSnapshot
}> = ({ companyHotel, current, currentMarketStateForDay }) => {

	return <div className="flex items-start gap-x-2">
		{current.isGroup ? <MarketStateForDayAvailabilityEvolutionIndicator current={current}
		                                                                    statDate={currentMarketStateForDay.statDate}
		                                                                    calculateDiff={(current, previous) => current.ratioAvailableHotelsOverTotal(companyHotel)[0] - previous.ratioAvailableHotelsOverTotal(companyHotel)[0]}>
				<DisplayAvailabilityIndexTag current={current} />
			</MarketStateForDayAvailabilityEvolutionIndicator>

			: <MarketStateForDayAvailabilityEvolutionIndicator current={current}
			                                                   statDate={currentMarketStateForDay.statDate}
			                                                   calculateDiff={(current, previous) => current.countAvailabilityEvolutionTotal() - previous.countAvailabilityEvolutionTotal()}>
				<DisplayAvailabilityIndexTag current={current} />
			</MarketStateForDayAvailabilityEvolutionIndicator>}
	</div>;
};

const RenderPmsAvailability = React.memo<{ tooltipId: string, companyHotel: CompanyHotel }>(({
																																															 tooltipId,
																																															 companyHotel,
																																														 }) => {

	const { availabilityDailyState, total } = useContext(AvailabilityDailyStateContext);
	const { totalRooms, findTotalRoomsByRoomName } = useContext(PricingRoomsInventoryContext);

	if (!availabilityDailyState || !availabilityDailyState.current) return null;

	const previousTotal = availabilityDailyState.previous?.items?.reduce((acc, room) => acc + room.availability, 0) ?? 0;
	const totalDifference = total - previousTotal;

	const renderDifference = (difference: number, showComparisonLabel = false) => {
		if (difference === 0 || !companyHotel.settings.calendar.numberOfDaysForPriceParity) {
			return null;
		}

		const Icon = difference > 0 ? ArrowUpIcon : ArrowDownIcon;
		const colorClassName = difference > 0 ? 'text-rose-500' : 'text-emerald-500';

		return <span className={`inline-flex items-center gap-x-0.5 text-xs font-medium ${colorClassName}`}>
			<Icon className="h-3 w-3" />
			<span>{Math.abs(difference)}</span>
			{showComparisonLabel && <span
				className="ml-0.5 text-[10px] font-medium text-slate-400">vs {companyHotel.settings.calendar.numberOfDaysForPriceParity}d</span>}
		</span>;
	};

	return <>
		<div className="inline-flex items-center gap-x-1.5">
			<Tag state="cyan" extraClassName="cursor-pointer" title="PMS availability">WH</Tag>
			<span className="cursor-pointer inline-flex items-center gap-x-1.5" data-tooltip-id={tooltipId}>
				<span>{renderDifference(totalDifference)}</span>
				<span className="text-xs text-slate-500">
					({total}{totalRooms !== undefined && `/${totalRooms}`})
				</span>
			</span>
		</div>
		<Tooltip
			id={tooltipId}
			openOnClick={true}
			opacity={100}
			className="z-50 !rounded-lg !bg-white !p-0 !opacity-100 shadow-xl ring-1 ring-black ring-opacity-5"
			style={{ backgroundColor: 'white' }}>
			<div className="w-72 overflow-hidden rounded-lg bg-white text-xs text-slate-500">
				<div className="flex items-start justify-between gap-x-4 px-4 py-3">
					<div className="flex items-baseline gap-x-2">
						<span className="text-xl font-semibold text-slate-900">
							{total}{totalRooms !== undefined &&
							<span className="text-xs font-semibold text-slate-400"> / {totalRooms}</span>}
						</span>
						<span className="font-medium text-slate-500">rooms available</span>
					</div>
					<div className="pt-1">{renderDifference(totalDifference, true)}</div>
				</div>
				<div className="border-t border-slate-100 px-4 py-3">
					<div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">By room type</div>
					<div className="space-y-2">
						{availabilityDailyState.current.items.map(room => {
							const roomTotal = findTotalRoomsByRoomName(room.roomName);

							return <div key={room.roomName} className="flex items-center justify-between gap-x-4">
								<span className="min-w-0 truncate text-xs font-medium text-slate-600">{room.roomName}</span>
								<span className="shrink-0 text-xs font-semibold text-slate-900">
									{room.availability}{roomTotal !== undefined &&
									<span className="text-[10px] font-semibold text-slate-400"> / {roomTotal}</span>}
								</span>
							</div>;
						})}
					</div>
				</div>
				<div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-[10px] font-medium text-slate-400">
					WH · Updated hourly · {Utilities.timeDifference(availabilityDailyState.current.creationDate)} ago
				</div>
			</div>
		</Tooltip>
	</>;
});

export default MarketStateSnapshotCalendarDayTableRow;
